// app/src/lib/procurement/normalizer.js — automatic item normalization (US-2.4).
//
// Match policy:
//   - Confidence ≥ 0.90 → auto-link, return { status: "linked", confidence, itemId }.
//   - Confidence in [0.60, 0.90) → propose, return { status: "propose", confidence, itemId }.
//   - Confidence < 0.60 → return { status: "unmatched" }; caller flags LineItem for review.
//
// Match signal sources (combined as max similarity):
//   1. VendorItem.normalizedVendorDescription exact equality (= 1.00).
//   2. Levenshtein similarity against any VendorItem.vendorDescription for this vendor.
//   3. Similarity against Item.normalizedName (cross-vendor) — penalized by 0.05.
//   4. SKU equality if both sides have one (= 1.00).

import { db } from '../db.js';
import { requireUserId, similarity, normalize } from './util.js';
import { upsertVendorItem } from './items.js';

const AUTO_LINK_THRESHOLD = 0.90;
const PROPOSE_THRESHOLD   = 0.60;
const CROSS_VENDOR_PENALTY = 0.05;

/**
 * @typedef {Object} NormalizeResult
 * @property {"linked"|"propose"|"unmatched"} status
 * @property {number} confidence
 * @property {string=} itemId
 * @property {string=} vendorItemId
 * @property {string=} matchedDescription
 */

/**
 * @param {{ vendorId:string, description:string, sku?:string }} input
 * @returns {Promise<NormalizeResult>}
 */
export async function classify(input) {
  if (!input.vendorId || !input.description) throw new Error('normalizer: vendorId+description required');
  const userId = await requireUserId();
  const desc = input.description;
  const norm = normalize(desc);

  // 1. Exact match on normalized vendor description.
  const exactByDesc = await db.vendorItems
    .where('vendorId').equals(input.vendorId)
    .filter((vi) => vi.userId === userId && vi.normalizedVendorDescription === norm)
    .first();
  if (exactByDesc) {
    return { status: 'linked', confidence: 1.0, itemId: exactByDesc.itemId, vendorItemId: exactByDesc.vendorItemId, matchedDescription: exactByDesc.vendorDescription };
  }

  // 2. Exact match on vendor SKU.
  if (input.sku) {
    const bySku = await db.vendorItems
      .where('vendorId').equals(input.vendorId)
      .filter((vi) => vi.userId === userId && vi.vendorSku === input.sku)
      .first();
    if (bySku) {
      return { status: 'linked', confidence: 1.0, itemId: bySku.itemId, vendorItemId: bySku.vendorItemId, matchedDescription: bySku.vendorDescription };
    }
  }

  // 3. Fuzzy match against this vendor's known descriptions.
  const vendorItems = await db.vendorItems
    .where('vendorId').equals(input.vendorId)
    .filter((vi) => vi.userId === userId && vi.isActive === 1)
    .toArray();

  let bestConf = 0;
  let bestVI = null;
  for (const vi of vendorItems) {
    const s = similarity(desc, vi.vendorDescription);
    if (s > bestConf) { bestConf = s; bestVI = vi; }
  }

  // 4. Fuzzy match against Item.normalizedName cross-vendor — with penalty.
  let bestCrossConf = 0;
  let bestCrossItemId = null;
  if (bestConf < AUTO_LINK_THRESHOLD) {
    const items = await db.items.where('[userId+isActive]').equals([userId, 1]).toArray();
    for (const it of items) {
      const s = similarity(desc, it.name);
      const adj = Math.max(0, s - CROSS_VENDOR_PENALTY);
      if (adj > bestCrossConf) { bestCrossConf = adj; bestCrossItemId = it.itemId; }
    }
  }

  let chosenItemId = bestVI?.itemId;
  let chosenVendorItemId = bestVI?.vendorItemId;
  let confidence = bestConf;
  let matched = bestVI?.vendorDescription;

  if (bestCrossConf > bestConf) {
    chosenItemId = bestCrossItemId;
    chosenVendorItemId = undefined;
    confidence = bestCrossConf;
    matched = undefined;
  }

  if (confidence >= AUTO_LINK_THRESHOLD) {
    // Record this mapping so the next invoice from the same vendor auto-matches.
    if (!chosenVendorItemId && chosenItemId) {
      const created = await upsertVendorItem({
        vendorId: input.vendorId,
        itemId: chosenItemId,
        vendorDescription: desc,
        vendorSku: input.sku,
      });
      chosenVendorItemId = created.vendorItemId;
    }
    return { status: 'linked', confidence, itemId: chosenItemId, vendorItemId: chosenVendorItemId, matchedDescription: matched };
  }
  if (confidence >= PROPOSE_THRESHOLD && chosenItemId) {
    return { status: 'propose', confidence, itemId: chosenItemId, vendorItemId: chosenVendorItemId, matchedDescription: matched };
  }
  return { status: 'unmatched', confidence };
}

/**
 * Confirm a user choice. Either links a vendor description to an existing item
 * (creating the VendorItem mapping) or creates a new item.
 *
 * @param {{ vendorId:string, description:string, sku?:string, packSize?:string, uom?:string }} input
 * @param {{ itemId:string }} choice
 */
export async function confirmMapping(input, choice) {
  if (!choice.itemId) throw new Error('confirmMapping: itemId required');
  const userId = await requireUserId();
  // Defense-in-depth: refuse to bind a vendor description to an item that
  // doesn't belong to the current user.
  const item = await db.items.get(choice.itemId);
  if (!item || item.userId !== userId) throw new Error('confirmMapping: item not found');
  return upsertVendorItem({
    vendorId: input.vendorId,
    itemId: choice.itemId,
    vendorDescription: input.description,
    vendorSku: input.sku,
    packSize: input.packSize,
    uom: input.uom,
  });
}

/** Returns the user's review queue: line items with no itemId set. */
export async function reviewQueue() {
  const userId = await requireUserId();
  return db.lineItems.where('userId').equals(userId).filter((li) => !li.itemId).toArray();
}
